import type { SpotifyClient } from './client.ts';
import type { Paged, SpotifyAlbumSimple, SpotifyImage, SpotifyShowSimple } from './types.ts';

export interface SearchHit {
  id: string;
  kind: 'album' | 'show';
  uri: string;
  title: string;
  subtitle: string;
  imageUrl: string;
  trackCount: number;
}

/** Waehlt ein moeglichst quadratisches Cover mittlerer Groesse. */
export function pickImage(images: SpotifyImage[] | undefined): string {
  if (!images?.length) return '';
  const sorted = [...images].sort((a, b) => (b.width ?? 0) - (a.width ?? 0));
  const medium = sorted.find((image) => (image.width ?? 0) <= 400);
  return (medium ?? sorted[0])?.url ?? '';
}

interface SearchResponse {
  albums?: Paged<SpotifyAlbumSimple>;
  shows?: Paged<SpotifyShowSimple>;
}

/**
 * Maximal erlaubte Trefferzahl pro Typ.
 *
 * Die Doku nennt 50, tatsaechlich antwortet die API ab 11 mit
 * „Invalid limit" (verifiziert). Deshalb bewusst hart auf 10.
 */
export const SEARCH_PAGE_SIZE = 10;

/**
 * Sucht Alben und Shows. Von Spotify kuratierte Playlists sind fuer neue Apps
 * nicht mehr abrufbar – Hoerspiele liegen ohnehin als Alben oder Shows vor.
 */
export async function searchCatalog(
  client: SpotifyClient,
  query: string,
  market: string,
  offset = 0,
): Promise<SearchHit[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  const result = await client.get<SearchResponse>('/search', {
    q: trimmed,
    type: 'album,show',
    market,
    limit: SEARCH_PAGE_SIZE,
    offset,
  });

  const albums: SearchHit[] = (result?.albums?.items ?? []).map((album) => ({
    id: album.id,
    kind: 'album',
    uri: album.uri,
    title: album.name,
    subtitle: album.artists.map((artist) => artist.name).join(', '),
    imageUrl: pickImage(album.images),
    trackCount: album.total_tracks,
  }));

  const shows: SearchHit[] = (result?.shows?.items ?? []).map((show) => ({
    id: show.id,
    kind: 'show',
    uri: show.uri,
    title: show.name,
    subtitle: show.publisher,
    imageUrl: pickImage(show.images),
    trackCount: show.total_episodes,
  }));

  return [...albums, ...shows];
}
